import { useState, useRef } from 'react';
import {
  Search,
  ShieldBan,
  ShieldX,
  ShieldAlert,
  ShieldOff,
  Plus,
  Trash2,
  Loader2,
  ChevronDown,
  X,
  Ban,
  Mail,
  MailX,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { parseDisplayName, formatDate } from '@/lib/utils';
import { useBlacklist, useAddToBlacklist, useRemoveFromBlacklist } from '@/api';
import type { BlacklistPerson } from '@/api';
import styles from './EmailBlacklist.module.css';

type FilterType = 'all' | 'manual' | 'bounced' | 'spam' | 'unsubscribed';

const FILTER_OPTIONS: { value: FilterType; label: string; icon: typeof ShieldBan }[] = [
  { value: 'all', label: 'All', icon: ShieldBan },
  { value: 'manual', label: 'Manual', icon: Ban },
  { value: 'bounced', label: 'Bounced', icon: MailX },
  { value: 'spam', label: 'Spam', icon: ShieldAlert },
  { value: 'unsubscribed', label: 'Unsubscribed', icon: ShieldOff },
];

const SuppressionBadge = ({ person }: { person: BlacklistPerson }) => {
  const badges: { label: string; className: string }[] = [];
  if (person.emailBlacklisted) badges.push({ label: 'Blacklisted', className: 'badgeManual' });
  if (person.emailBounced) badges.push({ label: 'Bounced', className: 'badgeBounced' });
  if (person.emailSpamReported) badges.push({ label: 'Spam', className: 'badgeSpam' });
  if (person.emailUnsubscribed) badges.push({ label: 'Unsubscribed', className: 'badgeUnsub' });
  return (
    <div className={styles.badgeGroup}>
      {badges.map((b) => (
        <span key={b.label} className={`${styles.badge} ${styles[b.className]}`}>
          {b.label}
        </span>
      ))}
    </div>
  );
};

export const EmailBlacklist = () => {
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [offset, setOffset] = useState(0);
  const [showAddForm, setShowAddForm] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [reasonInput, setReasonInput] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [confirmRemoveId, setConfirmRemoveId] = useState<number | null>(null);
  const prevFilterRef = useRef('');

  const { data, isLoading, isFetching } = useBlacklist(search, filterType, offset);
  const addMutation = useAddToBlacklist();
  const removeMutation = useRemoveFromBlacklist();

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const stats = data?.stats ?? { total: 0, manual: 0, bounced: 0, spam: 0, unsubscribed: 0 };

  // Reset offset when filters change
  const currentKey = `${search}|${filterType}`;
  if (currentKey !== prevFilterRef.current) {
    prevFilterRef.current = currentKey;
    if (offset !== 0) setOffset(0);
  }

  const handleAdd = async () => {
    const emails = emailInput
      .split(/[,\n]/)
      .map((e) => e.trim().toLowerCase())
      .filter((e) => e && e.includes('@'));

    if (emails.length === 0) {
      toast.error('Enter at least one valid email');
      return;
    }

    try {
      const result = await addMutation.mutateAsync({
        emails,
        reason: reasonInput || undefined,
      });
      toast.success(`${result.count} email(s) blacklisted`);
      setEmailInput('');
      setReasonInput('');
      setShowAddForm(false);
    } catch {
      toast.error('Failed to blacklist emails');
    }
  };

  const handleRemove = async (personId: number) => {
    try {
      await removeMutation.mutateAsync([personId]);
      toast.success('Removed from blacklist');
      setConfirmRemoveId(null);
    } catch {
      toast.error('Failed to remove');
    }
  };

  const handleBulkRemove = async () => {
    if (selectedIds.size === 0) return;
    try {
      await removeMutation.mutateAsync(Array.from(selectedIds));
      toast.success(`${selectedIds.size} removed from blacklist`);
      setSelectedIds(new Set());
    } catch {
      toast.error('Failed to remove');
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const manualItems = items.filter((i) => i.emailBlacklisted);
    if (selectedIds.size === manualItems.length && manualItems.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(manualItems.map((i) => i.id)));
    }
  };

  const hasMore = items.length + offset < total;

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerText}>
            <h1 className={styles.title}>Email Blacklist</h1>
            <p className={styles.subtitle}>
              Manage suppressed emails. Blacklisted contacts will never receive outreach emails.
            </p>
          </div>
          <button onClick={() => setShowAddForm(!showAddForm)} className={styles.addBtn}>
            {showAddForm ? (
              <>
                <X className={styles.addBtnIcon} /> Close
              </>
            ) : (
              <>
                <Plus className={styles.addBtnIcon} /> Add to Blacklist
              </>
            )}
          </button>
        </div>

        {/* Add form */}
        {showAddForm && (
          <div className={styles.addCard}>
            <div className={styles.addCardHeader}>
              <ShieldBan className={styles.addCardIcon} />
              <span className={styles.addCardTitle}>Add Emails to Blacklist</span>
            </div>
            <textarea
              className={styles.emailTextarea}
              placeholder="Enter email addresses (one per line or comma-separated)&#10;e.g. john@example.com, jane@example.com"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              rows={3}
            />
            <input
              type="text"
              className={styles.reasonInput}
              placeholder="Reason (optional) — e.g. Requested removal, competitor, invalid"
              value={reasonInput}
              onChange={(e) => setReasonInput(e.target.value)}
            />
            <div className={styles.addCardActions}>
              <span className={styles.addCardHint}>
                {emailInput.split(/[,\n]/).filter((e) => e.trim().includes('@')).length} email(s)
                detected
              </span>
              <button
                onClick={handleAdd}
                disabled={addMutation.isPending}
                className={styles.blacklistBtn}
              >
                {addMutation.isPending ? (
                  <Loader2 className={`${styles.addBtnIcon} ${styles.spinner}`} />
                ) : (
                  <ShieldBan className={styles.addBtnIcon} />
                )}
                Blacklist
              </button>
            </div>
          </div>
        )}

        {/* Stats cards */}
        <div className={styles.statsGrid}>
          <div className={`${styles.statCard} ${styles.statTotal}`}>
            <ShieldBan className={styles.statIcon} />
            <div className={styles.statValue}>{stats.total}</div>
            <div className={styles.statLabel}>Total Suppressed</div>
          </div>
          <div className={`${styles.statCard} ${styles.statManual}`}>
            <Ban className={styles.statIcon} />
            <div className={styles.statValue}>{stats.manual}</div>
            <div className={styles.statLabel}>Manual</div>
          </div>
          <div className={`${styles.statCard} ${styles.statBounced}`}>
            <MailX className={styles.statIcon} />
            <div className={styles.statValue}>{stats.bounced}</div>
            <div className={styles.statLabel}>Bounced</div>
          </div>
          <div className={`${styles.statCard} ${styles.statSpam}`}>
            <ShieldAlert className={styles.statIcon} />
            <div className={styles.statValue}>{stats.spam}</div>
            <div className={styles.statLabel}>Spam Reports</div>
          </div>
          <div className={`${styles.statCard} ${styles.statUnsub}`}>
            <ShieldOff className={styles.statIcon} />
            <div className={styles.statValue}>{stats.unsubscribed}</div>
            <div className={styles.statLabel}>Unsubscribed</div>
          </div>
        </div>

        {/* Filters */}
        <div className={styles.filtersCard}>
          <div className={styles.filterRow}>
            <div className={styles.searchWrapper}>
              <Search className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search by email, name, or business..."
                className={styles.searchInput}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className={styles.filterBtns}>
              {FILTER_OPTIONS.map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => setFilterType(value)}
                  className={`${styles.filterBtn} ${filterType === value ? styles.filterBtnActive : ''}`}
                >
                  {label}
                  {value !== 'all' && <span className={styles.filterBtnCount}>{stats[value]}</span>}
                </button>
              ))}
            </div>
          </div>
          <div className={styles.filterMeta}>
            <span className={styles.filterCount}>
              {total} result{total !== 1 ? 's' : ''}
            </span>
            {selectedIds.size > 0 && (
              <button
                onClick={handleBulkRemove}
                disabled={removeMutation.isPending}
                className={styles.bulkRemoveBtn}
              >
                <ShieldX className={styles.bulkRemoveIcon} />
                Remove {selectedIds.size} from blacklist
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className={styles.tableCard}>
          {isLoading ? (
            <div className={styles.loadingState}>
              <Loader2 className={styles.spinnerLg} />
              <span className={styles.loadingText}>Loading blacklist...</span>
            </div>
          ) : items.length === 0 ? (
            <div className={styles.emptyState}>
              <Mail className={styles.emptyIcon} />
              <p className={styles.emptyTitle}>
                {search || filterType !== 'all'
                  ? 'No results match your filters'
                  : 'Blacklist is empty'}
              </p>
              <p className={styles.emptyDesc}>
                {search || filterType !== 'all'
                  ? 'Try adjusting your search or filter.'
                  : 'Bounced, spam-reported, and manually blacklisted emails appear here.'}
              </p>
            </div>
          ) : (
            <>
              <div className={styles.tableWrapper}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th className={styles.checkboxCol}>
                        <input
                          type="checkbox"
                          className={styles.checkbox}
                          checked={
                            items.filter((i) => i.emailBlacklisted).length > 0 &&
                            selectedIds.size === items.filter((i) => i.emailBlacklisted).length
                          }
                          onChange={toggleSelectAll}
                        />
                      </th>
                      <th>Contact</th>
                      <th className={styles.hiddenSm}>Business</th>
                      <th>Status</th>
                      <th className={styles.hiddenMd}>Reason / Details</th>
                      <th className={styles.hiddenSm}>Date</th>
                      <th style={{ width: '3rem' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((person) => (
                      <tr key={person.id}>
                        <td className={styles.checkboxCol}>
                          {person.emailBlacklisted && (
                            <input
                              type="checkbox"
                              className={styles.checkbox}
                              checked={selectedIds.has(person.id)}
                              onChange={() => toggleSelect(person.id)}
                            />
                          )}
                        </td>
                        <td>
                          <div className={styles.contactName}>{person.fullName || 'Unknown'}</div>
                          <div className={styles.contactEmail}>{person.email}</div>
                        </td>
                        <td className={styles.hiddenSm}>
                          <span className={styles.businessName}>
                            {parseDisplayName(person.place?.displayName)?.text || '—'}
                          </span>
                        </td>
                        <td>
                          <SuppressionBadge person={person} />
                        </td>
                        <td className={`${styles.reasonCell} ${styles.hiddenMd}`}>
                          {person.emailBlacklistReason ||
                            (person.emailBounced
                              ? 'Auto: email bounced'
                              : person.emailSpamReported
                                ? 'Auto: spam reported'
                                : person.emailUnsubscribed
                                  ? 'Auto: unsubscribed'
                                  : '—')}
                        </td>
                        <td className={`${styles.dateCell} ${styles.hiddenSm}`}>
                          {formatDate(
                            person.emailBlacklistedAt || person.emailBouncedAt || person.createdAt,
                          )}
                        </td>
                        <td>
                          {person.emailBlacklisted &&
                            (confirmRemoveId === person.id ? (
                              <div className={styles.confirmRemove}>
                                <button
                                  onClick={() => handleRemove(person.id)}
                                  className={styles.confirmYes}
                                  title="Confirm remove"
                                >
                                  <ShieldX className={styles.actionIcon} />
                                </button>
                                <button
                                  onClick={() => setConfirmRemoveId(null)}
                                  className={styles.confirmNo}
                                  title="Cancel"
                                >
                                  <X className={styles.actionIcon} />
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setConfirmRemoveId(person.id)}
                                className={styles.removeBtn}
                                title="Remove from blacklist"
                              >
                                <Trash2 className={styles.actionIcon} />
                              </button>
                            ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {hasMore && (
                <div className={styles.loadMoreWrapper}>
                  <button
                    onClick={() => setOffset((prev) => prev + 20)}
                    disabled={isFetching}
                    className={styles.loadMoreBtn}
                  >
                    {isFetching ? (
                      <Loader2 className={`${styles.loadMoreIcon} ${styles.spinner}`} />
                    ) : (
                      <ChevronDown className={styles.loadMoreIcon} />
                    )}
                    Load more ({total - items.length - offset} remaining)
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
